/**
 * Exécutions (F2): the Airflow/Dagster-style run list. Polls GET /api/jobs
 * at the stats interval, narrows server-side to the start date chosen
 * (year, month, day — any combination), filters client-side by state
 * category and free text (pipeline name / job id), and keeps every filter
 * in the URL search params so a filtered view is shareable. Rows drill
 * down to /runs/:id.
 */
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { api } from "../lib/api";
import { useApiQuery } from "../hooks/useApiQuery";
import { usePageTitle } from "../hooks/usePageTitle";
import { statsPollIntervalMs } from "../lib/states";
import {
  RUN_FILTER_CATEGORIES,
  completeDateFilter,
  dateWindow,
  daysInMonth,
  filterJobs,
  filterYears,
  formatDateTime,
  isRunFilterCategory,
  jobsToCsv,
  parseDateFilter,
  type DateFilter,
  type RunFilterCategory,
} from "../lib/runs";
import { monthNames } from "../lib/params";
import { ID_SLICE_LEN, LS_DENSITY_KEY, RUNS_LIST_LIMIT } from "../lib/config";
import { PageHeader } from "../components/ui/PageHeader";
import { StateChip } from "../components/ui/StateChip";
import { DataTable } from "../components/ui/DataTable";
import { ErrorAlert } from "../components/ui/ErrorAlert";
import { EmptyState } from "../components/ui/EmptyState";
import { CopyIdButton } from "../components/ui/CopyIdButton";

type Density = "compact" | "comfortable";

function loadDensity(): Density {
  try {
    return window.localStorage.getItem(LS_DENSITY_KEY) === "comfortable"
      ? "comfortable"
      : "compact";
  } catch {
    return "compact";
  }
}

function saveDensity(density: Density) {
  try {
    window.localStorage.setItem(LS_DENSITY_KEY, density);
  } catch {
    /* private mode */
  }
}

/** The "any" choice of the date selects — the URL simply drops the key. */
const ANY = "";

export function RunsPage() {
  const { t, i18n } = useTranslation();
  usePageTitle("runs.title");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const catParam = searchParams.get("cat") ?? "all";
  const category: RunFilterCategory = isRunFilterCategory(catParam)
    ? catParam
    : "all";
  const search = searchParams.get("q") ?? "";
  const dateFilter = useMemo(() => parseDateFilter(searchParams), [searchParams]);
  const dateRange = useMemo(() => dateWindow(dateFilter), [dateFilter]);

  function updateParams(next: Record<string, string | undefined>) {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined) continue;
      if (value === "" || value === "all") params.delete(key);
      else params.set(key, value);
    }
    setSearchParams(params, { replace: true });
  }

  /**
   * A partial choice is written back complete — picking a day fills in this
   * month and this year — so the selects always show the window searched.
   */
  function updateDate(change: Partial<Record<keyof DateFilter, string>>) {
    const merged: DateFilter = { ...dateFilter };
    for (const key of ["year", "month", "day"] as const) {
      const raw = change[key];
      if (raw === undefined) continue;
      merged[key] = raw === ANY ? undefined : Number(raw);
    }
    // a month without a year or a day without a month make no window
    if (merged.year == null) merged.month = undefined;
    if (merged.month == null) merged.day = undefined;
    const full = completeDateFilter(merged);
    updateParams({
      y: full.year != null ? String(full.year) : "",
      m: full.month != null ? String(full.month) : "",
      d: full.day != null ? String(full.day) : "",
    });
  }

  const jobsQuery = useApiQuery(
    ["jobs", "list", RUNS_LIST_LIMIT, dateRange?.from ?? null, dateRange?.to ?? null],
    (signal) => api.jobs(RUNS_LIST_LIMIT, signal, dateRange ?? undefined),
    { pollMs: statsPollIntervalMs() },
  );
  const jobs = jobsQuery.data ?? null;

  const filtered = useMemo(
    () => filterJobs(jobs ?? [], { category, search }),
    [jobs, category, search],
  );

  const hasFilter = category !== "all" || search.trim() !== "" || dateRange != null;
  const [density, setDensity] = useState<Density>(loadDensity);

  const years = filterYears(dateFilter.year);
  const months = monthNames(i18n.language);
  const now = new Date();
  const dayCount = daysInMonth(
    dateFilter.year ?? now.getFullYear(),
    dateFilter.month ?? now.getMonth() + 1,
  );

  /** Client-side audit-trail export of the filtered list (normal <a download>). */
  function exportCsv() {
    // BOM so Excel decodes the UTF-8 accents in pipeline names.
    const blob = new Blob(["﻿" + jobsToCsv(filtered)], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `runs-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Stack spacing={3}>
      <PageHeader title={t("runs.title")} subtitle={t("runs.subtitle")} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label={t("runs.filterCategory")}
          value={category}
          onChange={(e) => updateParams({ cat: e.target.value })}
          sx={{ minWidth: 220 }}
        >
          {RUN_FILTER_CATEGORIES.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {t(`runs.cat.${cat}`)}
            </MenuItem>
          ))}
        </TextField>
        <Stack
          direction="row"
          spacing={1}
          role="group"
          aria-label={t("runs.dateFilterLabel")}
        >
          <TextField
            select
            size="small"
            label={t("runs.filterYear")}
            value={dateFilter.year != null ? String(dateFilter.year) : ANY}
            onChange={(e) => updateDate({ year: e.target.value })}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value={ANY}>{t("runs.anyYear")}</MenuItem>
            {years.map((year) => (
              <MenuItem key={year} value={String(year)}>
                {year}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t("runs.filterMonth")}
            value={dateFilter.month != null ? String(dateFilter.month) : ANY}
            onChange={(e) => updateDate({ month: e.target.value })}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value={ANY}>{t("runs.anyMonth")}</MenuItem>
            {months.map((name, index) => (
              <MenuItem key={name} value={String(index + 1)}>
                {name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t("runs.filterDay")}
            value={dateFilter.day != null ? String(Math.min(dateFilter.day, dayCount)) : ANY}
            onChange={(e) => updateDate({ day: e.target.value })}
            sx={{ minWidth: 110 }}
          >
            <MenuItem value={ANY}>{t("runs.anyDay")}</MenuItem>
            {Array.from({ length: dayCount }, (_, i) => i + 1).map((day) => (
              <MenuItem key={day} value={String(day)}>
                {day}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TextField
          size="small"
          label={t("runs.searchPlaceholder")}
          inputProps={{ "aria-label": t("runs.searchLabel") }}
          value={search}
          onChange={(e) => updateParams({ q: e.target.value })}
          sx={{ minWidth: 260 }}
        />
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ ml: { sm: "auto" } }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={density}
            aria-label={t("runs.densityLabel")}
            onChange={(_e, next: Density | null) => {
              if (!next) return;
              setDensity(next);
              saveDensity(next);
            }}
          >
            <ToggleButton value="compact" aria-label={t("runs.densityCompact")}>
              <Tooltip title={t("runs.densityCompact")}>
                <DensitySmallIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="comfortable" aria-label={t("runs.densityComfortable")}>
              <Tooltip title={t("runs.densityComfortable")}>
                <DensityMediumIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={filtered.length === 0}
            onClick={exportCsv}
          >
            {t("runs.exportCsv")}
          </Button>
        </Stack>
      </Stack>

      {/* Loading, error and data are three different screens. */}
      {!jobs && jobsQuery.isError && (
        <ErrorAlert error={jobsQuery.error} onRetry={() => void jobsQuery.refetch()} />
      )}
      {!jobs && !jobsQuery.isError && <LinearProgress />}

      {jobs && (
        <Card>
          <CardContent>
            {filtered.length === 0 ? (
              <EmptyState
                icon={<PlayCircleOutlineIcon fontSize="large" />}
                message={hasFilter ? t("runs.noMatch") : t("runs.empty")}
                actionLabel={hasFilter ? t("runs.resetFilters") : undefined}
                onAction={
                  hasFilter
                    ? () => setSearchParams(new URLSearchParams(), { replace: true })
                    : undefined
                }
              />
            ) : (
              <DataTable
                dense={density === "compact"}
                columns={[
                  { key: "state", label: t("runs.state") },
                  { key: "pipeline", label: t("runs.pipeline") },
                  { key: "created", label: t("runs.created") },
                  { key: "job", label: t("runs.job") },
                  { key: "details", label: "", align: "right" },
                ]}
              >
                {filtered.map((job) => (
                  <TableRow
                    key={job.job_id}
                    hover
                    onClick={() => navigate(`/runs/${job.job_id}`)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <StateChip
                        state={job.state}
                        fallbackLabel={job.dashboard_status}
                      />
                    </TableCell>
                    <TableCell>
                      {job.dag_name}
                      {job.forked_from_job_id && (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<CallSplitIcon />}
                          label={t("runs.forkOf", {
                            id: job.forked_from_job_id.slice(0, ID_SLICE_LEN),
                          })}
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate(`/runs/${job.forked_from_job_id}`);
                          }}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell className="num">
                      {formatDateTime(job.created_at, i18n.language)}
                    </TableCell>
                    <TableCell className="num">
                      {job.job_id.slice(0, ID_SLICE_LEN)}
                      <CopyIdButton value={job.job_id} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("runs.details")}>
                        <IconButton
                          size="small"
                          aria-label={t("runs.open", {
                            id: job.job_id.slice(0, ID_SLICE_LEN),
                          })}
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate(`/runs/${job.job_id}`);
                          }}
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </DataTable>
            )}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
